import { ref } from 'vue';
import pMap from 'p-map';
import { checkAssociationDeletePermission } from '@ilteducation/customer-mgmt-permissions';
import { CustomerMgmtTypes, useEditGroup, useEditUser, useSearchUsers } from '@ilteducation/customer-mgmt';
import { useAppEvents } from '../events/appEvents';
import { usePermissions } from './usePermissions';

const usedNames = ref<string[]>([]);

export function useManageGroup() {
  const {
    createGroup: create,
    updateGroup: theUpdateGroup,
    deleteGroup: theDeleteGroup,
    addMember,
    removeMember,
  } = useEditGroup();
  const { deleteAssociation } = useEditUser();
  const { fetchAllGroupMemebers } = useSearchUsers();
  const { permissionContext, reporterWithReason, reporterBoolean, createTargetAssociation, getCallerAssociation } =
    usePermissions();
  const { emitAppEvent } = useAppEvents();

  type usersToProcess = {
    uid: string;
    status: 'pending' | 'successful' | 'failed';
  };
  const usersToAdd = ref<usersToProcess[]>([]);
  const isAddingMembers = ref<boolean>(false);
  const addingMembersProgress = ref<number>(0);

  const memebersToRemove = ref<usersToProcess[]>([]);
  const isRemovingMembers = ref<boolean>(false);
  const removingMembersProgress = ref<number>(0);

  const isCreatingGroup = ref<boolean>(false);
  const isupdatingGroup = ref<boolean>(false);
  const isDeletingGroup = ref<boolean>(false);

  const addToUsedName = (name: string) => {
    if (!usedNames.value.includes(name)) {
      usedNames.value.push(name);
    }
  };

  const createGroup = async (
    organizationUid: string,
    name: string,
    type: CustomerMgmtTypes.GroupType,
    yearsInSchool: number | undefined,
    generateCode = false,
    expiresAt: string | null = null,
  ): Promise<CustomerMgmtTypes.CreateGroupResponse> => {
    isCreatingGroup.value = true;
    const createRes = await create({
      name,
      type,
      yearsInSchool,
      schoolUnitId: organizationUid,
      generateCode,
      expiresAt: expiresAt || undefined,
    }).catch((err: any) => {
      if (err?.response?.status === 409) {
        // Conflict: group name already used
        addToUsedName(name);
      } else {
        console.error('Failed to create group:', err);
      }
      isCreatingGroup.value = false;
      return Promise.reject(err);
    });

    isCreatingGroup.value = false;
    emitAppEvent({ type: 'group:created', groupUid: createRes.uid, organizationUid });
    return createRes;
  };

  const updateGroup = async (
    groupUid: string,
    updateGroupRequest: Omit<CustomerMgmtTypes.UpdateGroupRequest, 'groupUid'>,
  ): Promise<boolean> => {
    isupdatingGroup.value = true;
    const updateRes = await theUpdateGroup(groupUid, updateGroupRequest)
      .then(() => {
        isupdatingGroup.value = false;
        return true;
      })
      .catch((err: any) => {
        if (err?.response?.status === 409) {
          // Conflict: group name already used
          if (updateGroupRequest.name) addToUsedName(updateGroupRequest.name);
        } else {
          console.error('Failed to update group:', err);
        }
        isupdatingGroup.value = false;
        return false;
      });

    return updateRes;
  };

  const deleteGroup = async (groupUid: string, organizationUid?: string): Promise<boolean> => {
    isDeletingGroup.value = true;

    return theDeleteGroup(groupUid)
      .then(() => {
        isDeletingGroup.value = false;
        if (organizationUid) {
          emitAppEvent({ type: 'membership:updated', organizationUid });
        }
        return true;
      })
      .catch((err: any) => {
        console.error('Failed to delete group:', err);
        isDeletingGroup.value = false;
        return false;
      });
  };

  const addMemberToGroup = async (groupUid: string, userUid: string) => {
    const memeberToAdd = usersToAdd.value.find((m) => m.uid === userUid);

    return addMember(groupUid, userUid)
      .then(() => {
        if (memeberToAdd) memeberToAdd.status = 'successful';
        return true;
      })
      .catch((err: any) => {
        console.error('Failed to add member to group:', err);
        if (memeberToAdd) memeberToAdd.status = 'failed';
        return false;
      });
  };

  const addMembersToGroup = async (groupUid: string, userUids: string[]): Promise<usersToProcess[]> => {
    usersToAdd.value = userUids.map((uid) => ({ uid, status: 'pending' }));
    if (isAddingMembers.value) return usersToAdd.value;

    isAddingMembers.value = true;
    addingMembersProgress.value = 0;

    let index = 0;

    await pMap(
      usersToAdd.value,
      async (memeberToAdd) => {
        await addMemberToGroup(groupUid, memeberToAdd.uid);
        index++;
        addingMembersProgress.value = index;
      },
      { concurrency: 3 },
    );

    isAddingMembers.value = false;

    return usersToAdd.value;
  };

  const removeMemberFromGroup = async (groupUid: string, userUid: string) => {
    const memeberToRemove = memebersToRemove.value.find((m) => m.uid === userUid);

    return removeMember(groupUid, userUid)
      .then(() => {
        if (memeberToRemove) memeberToRemove.status = 'successful';
        return true;
      })
      .catch((err: any) => {
        console.error('Failed to remove member from group:', err);
        if (memeberToRemove) memeberToRemove.status = 'failed';
        return false;
      });
  };

  const removeMembersFromGroup = async (groupUid: string, userUids: string[]): Promise<usersToProcess[]> => {
    memebersToRemove.value = userUids.map((uid) => ({ uid, status: 'pending' }));
    if (isRemovingMembers.value) return memebersToRemove.value;

    isRemovingMembers.value = true;
    removingMembersProgress.value = 0;

    let processedMembers = 0;
    await pMap(
      memebersToRemove.value,
      async (memeberToRemove) => {
        await removeMemberFromGroup(groupUid, memeberToRemove.uid);
        processedMembers++;
        removingMembersProgress.value = processedMembers;
      },
      { concurrency: 3 },
    );

    isRemovingMembers.value = false;
    return memebersToRemove.value;
  };

  // check permisison to delete users in group and count user by type in group
  const checkDeleteUsersInGroup = async (groupUid: string, organizationUid: string) => {
    type CheckDeleteUsersInGroupResponse = {
      allowed: string[];
      [key: string]: string[]; // not allowed reason as key
    };
    const response: CheckDeleteUsersInGroupResponse = {
      allowed: [] as string[],
    };
    const memberUsers = await fetchAllGroupMemebers(groupUid);
    if (!memberUsers || memberUsers.length === 0) {
      return response;
    }

    for (const memberUser of memberUsers) {
      const memberAssoc = memberUser.associations.find((a) => a.organization.uid === organizationUid);
      if (!memberAssoc) {
        console.error('UMGDGM01: No association found for user', memberUser.uid);
        continue;
      }
      if (memberAssoc.role !== 'student') {
        continue;
      }

      const deleteAssocCheck = await checkAssociationDeletePermission(
        permissionContext.value,
        createTargetAssociation(memberUser, memberAssoc),
        getCallerAssociation,
        reporterWithReason,
      );

      if (!deleteAssocCheck.allowed && !response[deleteAssocCheck.reason]) {
        response[deleteAssocCheck.reason] = [];
      }
      response[deleteAssocCheck.allowed ? 'allowed' : deleteAssocCheck.reason].push(memberUser.uid);
    }

    return response;
  };

  /**
   * Permantly delete all student users that are members of the specified group.
   *
   * @param groupUid Group UID from which members will be deleted
   * @param organizationUid Organization UID to which the group belongs
   */
  const deleteGroupWithStudents = async (groupUid: string, organizationUid: string) => {
    const memberUsers = await fetchAllGroupMemebers(groupUid);
    if (!memberUsers || memberUsers.length === 0) {
      // No members in the group, just delete the group
      return deleteGroup(groupUid, organizationUid);
    }
    const usersWithDeletedAssociations: string[] = [];
    await pMap(
      memberUsers,
      async (memberUser) => {
        const memberAssoc = memberUser.associations.find((a) => a.organization.uid === organizationUid);
        if (!memberAssoc) {
          console.error('UMGDGM01: No association found for user', memberUser.uid);
          return;
        }
        if (memberAssoc.role !== 'student') {
          // when group is deleted, employees and gurdians memberships will be removed automaticlly
          return;
        }

        const canDeleteAssociation = await checkAssociationDeletePermission(
          permissionContext.value,
          createTargetAssociation(memberUser, memberAssoc),
          getCallerAssociation,
          reporterBoolean,
        );
        if (canDeleteAssociation) {
          await deleteAssociation(memberUser.uid, organizationUid);
          usersWithDeletedAssociations.push(memberUser.uid);
        }
      },
      { concurrency: 5 },
    );

    if (usersWithDeletedAssociations.length > 0) {
      emitAppEvent({ type: 'association:updated', userUids: usersWithDeletedAssociations });
    }

    return deleteGroup(groupUid, organizationUid);
  };

  return {
    usedNames,
    isCreatingGroup,
    isAddingMembers,
    addingMembersProgress,
    isRemovingMembers,
    removingMembersProgress,
    createGroup,
    updateGroup,
    deleteGroup,
    addMemberToGroup,
    removeMemberFromGroup,
    addMembersToGroup,
    removeMembersFromGroup,
    deleteGroupWithStudents,
    checkDeleteUsersInGroup,
  };
}
